import React from "react";
import { LucideIcon } from "lucide-react";
import "./styles.css";

export type ButtonVariant = "primary" | "ghost" | "outline";
export type ButtonSize = "small" | "medium" | "large";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon | string;
  iconColor?: string;
  iconAlt?: string;
  iconPosition?: "left" | "right";
  loading?: boolean;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderStyle?: string;
  textColor?: string;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "small",
  icon,
  iconColor,
  iconAlt = "",
  iconPosition = "left",
  loading = false,
  backgroundColor,
  borderColor,
  borderWidth,
  borderStyle,
  textColor,
  fullWidth = false,
  disabled,
  children,
  className = "",
  ...props
}) => {
  const buttonClasses = [
    "btn",
    `btn--${variant}`,
    `btn--${size}`,
    fullWidth && "btn--full-width",
    loading && "btn--loading",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const customStyles: React.CSSProperties = {
    ...(backgroundColor && { backgroundColor }),
    ...(borderColor && { borderColor }),
    ...(borderWidth && { borderWidth }),
    ...(borderStyle && { borderStyle }),
  };

  const getIconSize = () => {
    switch (size) {
      case "small":
        return 16;
      case "large":
        return 24;
      default:
        return 22;
    }
  };

  const renderIcon = () => {
    if (!icon || loading) return null;

    // Check if icon is a string (SVG path) or a Lucide component
    if (typeof icon === "string") {
      return <img src={icon} alt={iconAlt} className="btn-icon" />;
    }

    // It's a Lucide icon component
    const IconComponent = icon;
    return <IconComponent className="btn-icon" size={getIconSize()} color={iconColor} />;
  };

  return (
    <button className={buttonClasses} disabled={disabled || loading} style={customStyles} {...props}>
      {loading && <span className="btn-loader" />}
      {iconPosition === "left" && renderIcon()}
      {children && (
        <span className="btn-text" style={{ color: textColor }}>
          {children}
        </span>
      )}
      {iconPosition === "right" && renderIcon()}
    </button>
  );
};

export default Button;
